/**
 * Org standards — read model for the settings-org-standards feature.
 *
 * Org standards is the org's single global markdown document (Layer 1 of the
 * agent's layered context). Exactly one `org_standards` row per org — a
 * singleton, not a per-role collection like `role_rules`. Turn 10.2a ships the
 * read-only single-pane view shell + Edit-tab working copy; the live Preview
 * lands in 10.2b, the Save / Archive RPCs in 10.2c–10.2d, version History in
 * 10.2e.
 */

/** Soft length warning threshold — no hard cap (per the prd). */
export const STANDARDS_SOFT_WARN = 4000;

/**
 * Starter examples shown in the Edit-tab empty-state banner. Clicking one
 * seeds it into the working copy as a basis for the first standard. Mirrors the
 * examples listed in the settings-org-standards ux spec § Edit tab.
 */
export const STARTER_EXAMPLES: readonly string[] = [
  'We write specs that read top-to-bottom: problem, scope, plan, edge cases, then open questions.',
  'Always run lints before suggesting code.',
  'Never delete tests without explicit user approval.'
];

export type OrgStandardStatus = 'authored' | 'empty' | 'archived';

export interface OrgStandard {
  /** `org_standards.id`, or null when no row exists yet (the org has never
   *  authored standards — the singleton is created on first Save in 10.2d). */
  id: string | null;
  /** Owning org id. */
  orgId: string;
  /** Markdown body. Empty string when no row exists. */
  content: string;
  /** ISO timestamp; display-only. Null when never propagated. */
  lastPropagatedAt: string | null;
  /** ISO timestamp; soft-delete. Null when the standards doc is active. */
  archivedAt: string | null;
}

/**
 * Resolves the status chip for the org standards doc:
 * - `archived` — the row is soft-deleted; the agent receives no Layer 1.
 * - `empty` — no row, or whitespace-only content; the agent receives no
 *   Layer 1 (org standards has no platform-defined default — when absent it
 *   simply isn't sent).
 * - `authored` — an org-defined standards doc exists.
 */
export function resolveStandardStatus(std: OrgStandard): OrgStandardStatus {
  if (std.archivedAt) return 'archived';
  if (std.content.trim() === '') return 'empty';
  return 'authored';
}

export function standardStatusLabel(status: OrgStandardStatus): string {
  if (status === 'authored') return 'Authored';
  if (status === 'archived') return 'Archived';
  return 'Empty';
}

/**
 * One immutable snapshot in `org_standard_versions`. Appended on every Save,
 * Archive/Restore, and version-Restore. `restoredFromVersionId` is null for an
 * ordinary snapshot; it points at the source snapshot when a version-Restore
 * produced this row. Loaded by the VM in 10.2d; consumed by the History tab in
 * 10.2e.
 */
export interface OrgStandardVersion {
  /** `org_standard_versions.id`. */
  id: string;
  /** `org_standards.id` this snapshot belongs to. */
  orgStandardId: string;
  /** Monotonic per-row version number. */
  versionNumber: number;
  /** Snapshotted markdown body. */
  content: string;
  /** Source snapshot id when this row was produced by a version-Restore. */
  restoredFromVersionId: string | null;
  /** ISO timestamp. */
  createdAt: string;
  /** Author user id, or null. */
  createdBy: string | null;
}

/** Newest first — by version number descending. */
export function sortOrgStandardVersions(
  versions: readonly OrgStandardVersion[]
): OrgStandardVersion[] {
  return [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
}
